import { useMemo } from 'react';
import { GraphQLTaggedNode } from 'relay-runtime';
import {
	useQueryInterop,
	type UseQueryInteropSupportedHookOptions,
	type UseQueryInteropSupportedResult,
} from './useQueryInterop';

type FragmentSelector<TQueryData> = (data: TQueryData) => any;

export type FragmentRef = {
	__typename: string;
	id: string;
};

type UseFragmentizedQueryResult<TQueryData, TVariables> = UseQueryInteropSupportedResult<
	TQueryData,
	TVariables
> & {
	fragmentRefs: {
		[key: string]: FragmentRef;
	};
};
export const useFragmentizedQuery = <TQueryData, TVariables extends Record<string, any>>(
	query: GraphQLTaggedNode,
	params: UseQueryInteropSupportedHookOptions<TQueryData, TVariables>,
	selectors?: Record<string, FragmentSelector<TQueryData>>,
): UseFragmentizedQueryResult<TQueryData, TVariables> => {
	const result = useQueryInterop<TQueryData, TVariables>(query, params);

	const { data, loading } = result;

	const fragmentRefs = useMemo(() => {
		// Don't compute fragmentRefs during loading or when data is not available
		if (loading || !data) {
			return {} as UseFragmentizedQueryResult<TQueryData, TVariables>['fragmentRefs'];
		}

		const fragmentRefsResult = {} as UseFragmentizedQueryResult<
			TQueryData,
			TVariables
		>['fragmentRefs'];

		for (const key in selectors) {
			const selector = selectors?.[key];

			// Use selector if provided, otherwise fall back to direct key access
			const entity = selector ? selector(data as TQueryData) : (data as any)?.[key];

			if (!entity) {
				throw new Error(
					`useFragmentizedQuery: Expected entity for fragment key "${String(key)}" but it was not found in the query data. Make sure the query includes the necessary fields for this fragment.`,
				);
			}

			if (!entity.__typename || !entity.id) {
				throw new Error(
					`useFragmentizedQuery: Entity for fragment key "${String(key)}" is missing required fields (__typename or id).`,
				);
			}

			fragmentRefsResult[key] = {
				__typename: entity.__typename,
				id: entity.id,
			} as FragmentRef;
		}

		return fragmentRefsResult;
	}, [data, loading, selectors]);

	return {
		...result,
		fragmentRefs,
	};
};
